import React, {useEffect, useRef, useState} from "react";
import styled from "@emotion/styled";
import gsap from "gsap";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";
import ExpoScaleEase from "gsap/EasePack";
import CustomEase from "gsap/CustomEase";
import {TQFont, tqtypes} from "@coretech/typequestweb";
import useFontStore from "../stores/useFontStore";
import useJoystickStore from "../stores/useJoystickStore";
import {Directions, MAX_MAP_POSITION, MIN_MAP_POSITION, Speeds, THROW_RESISTANCE} from "../utils/constants";
import useStateRef from "../hooks/useStateRef";
import useInterval from "../hooks/useInterval";
import useTQWeb from "../hooks/useTQWeb";
import {ApexFont, Coordinates} from "../utils/interfaces";
import joystickBase from "../assets/images/joystick-base.png";
import joystickInner from "../assets/images/joystick-inner.png";
import joystickHandle from "../assets/images/joystick-handle.svg";
import fontMapData from "../data/ApexFontMap.json";
import useAppStore from "../stores/useAppStore";
import {getCoordsInsideRef} from "../utils/windowUtils";
import JoystickSlices from "./JoystickSlices";
import {findNonEmptyForDirection, getNeighbors, manhattanAdjacencyArray} from "../utils/fontUtils";
import {Dir} from "fs";

gsap.registerPlugin(Draggable, InertiaPlugin, ExpoScaleEase, CustomEase);

const JoystickStyled = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "140px",
    height: "140px",
    backgroundImage: `url(${joystickBase})`,
    backgroundSize: "140px 140px",
    borderRadius: "70px",
    userSelect: "none",
    cursor: "pointer"
});

const Handle = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "83px",
    height: "83px",
    backgroundImage: `url(${joystickHandle})`,
    borderRadius: "41.5px",
    zIndex: "3",
    userSelect: "none",
    [`:focus`]: {
        outline: "5px dashed #669933",
    }
});

// @ts-ignore
const data: ApexFont[] = fontMapData;

function Joystick() {

    //#region VARS
    const rootRef = useRef<HTMLDivElement>(null);
    const baseRef = useRef<HTMLDivElement>(null);
    const handleRef = useRef<HTMLDivElement>(null);

    const isInitialized = useAppStore(state => state.isInitialized);
    const isLoaded = useAppStore(state => state.isLoaded);

    const tqWeb = useTQWeb();
    // used to calc the joystick direction and scroll speed
    const [joystickCenter, setJoystickCenter, joystickCenterRef] = useStateRef({x: 0, y: 0});
    const [handleDelta, setHandleDelta] = useState<Coordinates>({x: 0, y: 0});

    const [isDragging, setIsDragging] = useState(false);
    const [isThrowing, setIsThrowing] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const scrollDirection = useJoystickStore(state => state.scrollDirection);
    const scrollSpeed = useJoystickStore(state => state.scrollSpeed);
    const speedThresholds = useJoystickStore(state => state.speedThresholds);
    const speedThrottles = useJoystickStore(state => state.speedThrottles);

    const currX = useFontStore(state => state.currX);
    const currY = useFontStore(state => state.currY);
    const setCurrX = (x: number) => useFontStore.setState({currX: x});
    const setCurrY = (y: number) => useFontStore.setState({currY: y});

    const [currFont, setCurrFont] = useState<ApexFont>({} as ApexFont);
    const [gsapCurrFont, setGsapCurrFont, gsapCurrFontRef] = useStateRef({} as ApexFont);

    const [multipleAtPosition, setMultipleAtPosition] = useState(false);
    const [x, setX] = useState({count: currX});
    //#endregion

    const clipAtRadius = (radius: number, offsetX: number, offsetY: number): any => {
        return (point: { x: number; y: number; }) => {
            let x = point.x - offsetX,
                y = point.y - offsetY,
                length = Math.sqrt(x * x + y * y),
                angle;
            if (length > radius) {
                angle = Math.atan2(y, x);
                point.x = radius * Math.cos(angle);
                point.y = radius * Math.sin(angle);
            }
            return point;
        };
    };

    const getAngle = (dX: number, dY: number) => {
        let radians = Math.atan2(dY, dX);
        if (radians < 0) radians += 2 * Math.PI;
        return radians * (180 / Math.PI);
    };

    const getDirection = (angle: number) => {
        let dir = scrollDirection;
        if (angle >= 335 || angle <= 25) dir = Directions.RIGHT;
        if (angle > 25 && angle < 65) dir = Directions.DOWN_RIGHT;
        if (angle >= 65 && angle <= 115) dir = Directions.DOWN;
        if (angle > 115 && angle < 155) dir = Directions.DOWN_LEFT;
        if (angle >= 155 && angle <= 205) dir = Directions.LEFT;
        if (angle > 205 && angle < 245) dir = Directions.UP_LEFT;
        if (angle >= 245 && angle <= 295) dir = Directions.UP;
        if (angle > 295 && angle < 335) dir = Directions.UP_RIGHT;
        return dir;
    };

    const setCenter = () => {
        if (baseRef.current) {
            let baseRect = baseRef.current.getBoundingClientRect();
            let baseX = baseRect.left + (baseRect.width / 2);
            let baseY = baseRect.top + (baseRect.height / 2);
            setJoystickCenter({x: baseX, y: baseY});
        }
    };

    const onClick = (e: React.MouseEvent) => {
        let coords = getCoordsInsideRef(baseRef, {x: e.clientX, y: e.clientY}, true);
        /*console.log('e', e);
        console.log('coords', coords);*/
        let angle = getAngle(coords.x, coords.y);
        let dir = getDirection(angle);
        //useFontStore.getState().addToHistory(currFont);
        useJoystickStore.setState({scrollDirection: dir});
        onMove(dir);
    };

    const loadFont = (apexFont: ApexFont) => {
        if (tqWeb !== undefined && !apexFont.isLoaded) {
            tqWeb.findFonts(
                [apexFont._postscriptName],
                tqtypes.OperationMode.DATA,
                "ABCDEFGHIJKLMNOPQRSTUV abcdefghijklmnopqrstuvwxyz0123456789,.;:[]{}|~'!?<>@#$%^&*()_+-=\"/",
                (fontInfo: TQFont, error: tqtypes.Error) => {
                    if (error.errCode !== 0) {
                        console.error("error", apexFont._postscriptName, error);
                        //if(!isLoaded && !apexFont.isLoaded) loadFont(apexFont);
                    } else {
                        if(apexFont.isLoaded) return true;
                        const font: any = new FontFace(
                            fontInfo.postscriptName.replaceAll('.', '-'),
                            fontInfo.fontBuffer
                        );
                        font.load().then(function () {
                            if (!isLoaded) useAppStore.setState({isLoaded: true});
                            if(apexFont.isLoaded) return;
                            document.fonts.add(font);
                            apexFont.isLoaded = true;
                        }).catch((e: any) => {
                            console.error("error", e);
                        });
                    }
                    return true;
                }
            );
        }
    };
    const [firstLoad, setFirstLoad] = useState(true);
    const loadAdjacent = () => {
        let find = data.find(font => font.x === currX && font.y === currY);
        if (find !== undefined) {
            loadFont(find);

            let f;
            const d = findNonEmptyForDirection(find.x, find.y, Directions[scrollDirection]);
            //console.log('d', d);
            f = data.find(font => font.x === d.x && font.y === d.y);
            if(f !== undefined) loadFont(f);

            for(let dir in Directions) {
                if (isNaN(Number(dir))) {
                    const next = findNonEmptyForDirection(find.x, find.y, dir);
                    //console.log('next', next, Directions[d]);
                    f = data.find(font => font.x === next.x && font.y === next.y);
                    if(f !== undefined) loadFont(f);
                }
            }
        }

        let n = 0;
        if (firstLoad) {
            setFirstLoad(false);
            n = 4;
        }
        let adjacencyArray = manhattanAdjacencyArray(n);
        let neighbors = getNeighbors(currX, currY, adjacencyArray);
        //console.log(neighbors)
        neighbors.map(coord => {
            let f = data.find(font => font.x === coord.x && font.y === coord.y);
            if (f !== undefined) loadFont(f);
        });
    };

    useEffect(() => {
        if (!isDragging && currFont._postscriptName !== undefined) {
            /*console.log('currFont', currFont._postscriptName)
            console.log('prevFontStr', useFontStore.getState().prevFontStr)
            console.log('currFontStr', useFontStore.getState().currFontStr)*/
            /*useFontStore.setState({currFont: currFont});
            useFontStore.setState({currFontStr: currFont._postscriptName});*/
            //useFontStore.setState({currFontStr: currFont._postscriptName});
        }
    }, [isDragging, currFont]);

    useEffect(() => {
        setCenter();

        let ctx = gsap.context(() => {
            Draggable.create(handleRef.current, {
                type: "x,y",
                inertia: {
                    resistance: THROW_RESISTANCE
                },
                liveSnap: {
                    points: clipAtRadius(speedThresholds[2], 0, 0)
                },
                minimumMovement: 5,
                snap: {
                    x: function (endValue) {
                        return 0;
                    },
                    y: function (endValue) {
                        return 0;
                    }
                },
                onPress() {
                    gsap.killTweensOf("#handle");
                    this.update();
                },
                onThrowComplete: function () {
                    setIsThrowing(false);
                    useJoystickStore.setState({scrollDirection: Directions.NEUTRAL});
                },
                onDragStart: function (e) {
                    //useFontStore.getState().addToHistory(gsapCurrFontRef.current);
                    setCenter();
                    setIsDragging(true);
                    useJoystickStore.setState({isDragging: true});
                },
                onDrag: function (e) {
                    let center = joystickCenterRef.current;
                    if (handleRef.current) {
                        let handleRect = handleRef.current.getBoundingClientRect();
                        let handleX = handleRect.left + (handleRect.width / 2);
                        let handleY = handleRect.top + (handleRect.height / 2);
                        let dX = handleX - center.x;
                        let dY = handleY - center.y;
                        setHandleDelta({x: dX, y: dY});
                    }
                },
                onDragEnd: function () {
                    //console.log('dur', this.tween)
                    setIsDragging(false);
                    useJoystickStore.setState({isDragging: false});

                    gsap.to("#handle", {duration: 0.150, x: 0, y: 0});

                    useJoystickStore.setState({scrollDirection: Directions.NEUTRAL});
                }
            });
        }, rootRef);

        return () => ctx.revert();
    }, []);

    useEffect(() => {
        onMove(scrollDirection);
    }, [x.count]);

    useEffect(() => {
        const dX = Math.abs(handleDelta.x);
        const dY = Math.abs(handleDelta.y);

        useJoystickStore.setState({deltaX: handleDelta.x, deltaY: handleDelta.y});

        let angle = getAngle(handleDelta.x, handleDelta.y);
        //console.log('angle', angle);
        if (handleDelta.x !== 0 || handleDelta.y !== 0)
            useJoystickStore.setState({scrollDirection: getDirection(angle)});

        let speed = scrollSpeed;
        if ((dX > 0 && dX < speedThresholds[0] + 1) || (dY > 0 && dY < speedThresholds[0] + 1)) speed = Speeds.SLOW;
        if ((dX > speedThresholds[0] && dX < speedThresholds[1] + 1) || (dY > speedThresholds[0] && dY < speedThresholds[1] + 1)) speed = Speeds.MEDIUM;
        if ((dX > speedThresholds[1] && dX < speedThresholds[2] + 1) || (dY > speedThresholds[1] && dY < speedThresholds[2] + 1)) speed = Speeds.FAST;
        if ((dX > speedThresholds[2]) || (dY > speedThresholds[2])) speed = Speeds.SUPER;
        useJoystickStore.setState({scrollSpeed: speed});
    }, [handleDelta]);

    useInterval(() => {
        if (isDragging) onMove(scrollDirection);
    }, speedThrottles[scrollSpeed]);

    const onMove = (dir: Directions) => {
        let x = currX;
        let y = currY;
        switch (dir) {
            case Directions.RIGHT:
                x = currX + 1;
                break;
            case Directions.DOWN_RIGHT:
                x = currX + 1;
                y = currY + 1;
                break;
            case Directions.DOWN:
                y = currY + 1;
                break;
            case Directions.DOWN_LEFT:
                x = currX - 1;
                y = currY + 1;
                break;
            case Directions.LEFT:
                x = currX - 1;
                break;
            case Directions.UP_LEFT:
                x = currX - 1;
                y = currY - 1;
                break;
            case Directions.UP:
                y = currY - 1;
                break;
            case Directions.UP_RIGHT:
                x = currX + 1;
                y = currY - 1;
                break;
        }

        if (x < MIN_MAP_POSITION) x = MAX_MAP_POSITION;
        if (x > MAX_MAP_POSITION) x = MIN_MAP_POSITION;
        setCurrX(x);

        if (y < MIN_MAP_POSITION) y = MAX_MAP_POSITION;
        if (y > MAX_MAP_POSITION) y = MIN_MAP_POSITION;
        setCurrY(y);

        useFontStore.setState({currMapPosition: {x: x, y: y}});
    };

    const isPositionEmpty = (x: number, y: number): boolean =>
        Boolean(data.find(font => font.x === x && font.y === y) === undefined);

    const findNextFontForDirection = (direction?: Directions) => {
        let found = false;
        let x = currX;
        let y = currY;
        let dir = scrollDirection;
        if (direction) dir = direction;
        switch (dir) {
            case Directions.RIGHT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                    } else {
                        if (x + 1 > MAX_MAP_POSITION) x = MIN_MAP_POSITION;
                        else x++;
                    }
                }
                break;
            case Directions.DOWN_RIGHT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                        setCurrY(y);
                    } else {
                        if (x + 1 > MAX_MAP_POSITION) x = MIN_MAP_POSITION;
                        else x++;
                        if (y + 1 > MAX_MAP_POSITION) y = MIN_MAP_POSITION;
                        else y++;
                    }
                }
                break;
            case Directions.DOWN:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrY(y);
                    } else {
                        if (y + 1 > MAX_MAP_POSITION) y = MIN_MAP_POSITION;
                        else y++;
                    }
                }
                break;
            case Directions.DOWN_LEFT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                        setCurrY(y);
                    } else {
                        if (x - 1 < MIN_MAP_POSITION) x = MAX_MAP_POSITION;
                        else x--;
                        if (y + 1 > MAX_MAP_POSITION) y = MIN_MAP_POSITION;
                        else y++;
                    }
                }
                break;
            case Directions.LEFT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                    } else {
                        if (x - 1 < MIN_MAP_POSITION) x = MAX_MAP_POSITION;
                        else x--;
                    }
                }
                break;
            case Directions.UP_LEFT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                        setCurrY(y);
                    } else {
                        if (x - 1 < MIN_MAP_POSITION) x = MAX_MAP_POSITION;
                        else x--;
                        if (y - 1 < MIN_MAP_POSITION) y = MAX_MAP_POSITION;
                        else y--;
                    }
                }
                break;
            case Directions.UP:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrY(y);
                    } else {
                        if (y - 1 < MIN_MAP_POSITION) y = MAX_MAP_POSITION;
                        else y--;
                    }
                }
                break;
            case Directions.UP_RIGHT:
                while (!found) {
                    found = !isPositionEmpty(x, y);
                    if (found) {
                        setCurrX(x);
                        setCurrY(y);
                    } else {
                        if (x + 1 > MAX_MAP_POSITION) x = MIN_MAP_POSITION;
                        else x++;
                        if (y - 1 < MIN_MAP_POSITION) y = MAX_MAP_POSITION;
                        else y--;
                    }
                }
                break;
        }
    };

    useEffect(() => {
        if (!isInitialized) return;

        //console.log("x,y", currX, currY);

        if (isPositionEmpty(currX, currY)) {
            findNextFontForDirection();
            return;
        }

        setMultipleAtPosition(data.filter(font => font.x === currX && font.y === currY).length > 1);

        let find = data.find(font => font.x === currX && font.y === currY);
        if (find !== undefined) {
            useFontStore.getState().addToHistory(find);
            loadAdjacent();
            setCurrFont(find);
            setGsapCurrFont(find);
            useFontStore.setState({currFont: find});
            useFontStore.setState({
                prevFontStr: useFontStore.getState().currFontStr,
                currFontStr: find._postscriptName.replaceAll('.', '-')
            });
            //console.log('psName:', find._postscriptName, document.fonts.check(`12px ${find._postscriptName}`));
            if (!isDragging)
                setTimeout(() => useJoystickStore.setState({scrollDirection: Directions.NEUTRAL}), 100);
        }
    }, [isInitialized, currX, currY]);

    const onKey = (e: KeyboardEvent) => {
        e.preventDefault();
        switch (e.key) {
            case "ArrowLeft":
                useJoystickStore.setState({scrollDirection: Directions.LEFT});
                onMove(Directions.LEFT);
                break;
            case "ArrowRight":
                useJoystickStore.setState({scrollDirection: Directions.RIGHT});
                onMove(Directions.RIGHT);
                break;
            case "ArrowUp":
                useJoystickStore.setState({scrollDirection: Directions.UP});
                onMove(Directions.UP);
                break;
            case "ArrowDown":
                useJoystickStore.setState({scrollDirection: Directions.DOWN});
                onMove(Directions.DOWN);
                break;
        }
    };
    useEffect(() => {
        //console.log("isFocused", isFocused)
        if (handleRef.current) {
            if (isFocused) handleRef.current.addEventListener("keydown", onKey, false);
            else handleRef.current.removeEventListener("keydown", onKey, false);
        }

        return () => {
            if (handleRef.current) handleRef.current.removeEventListener("keydown", onKey, false);
        };
    }, [isFocused, currX, currY]);

    return (
        <div ref={rootRef}>
            <JoystickStyled ref={baseRef} id="joystick" onClick={onClick}>
                <div style={{position: "absolute", transform: "translateY(3.5px)", zIndex: 0}}>
                    <img src={joystickInner} alt="joystick inner"/>
                </div>
                <div style={{position: "absolute", zIndex: 1}}>
                    <JoystickSlices/>
                </div>
                <Handle ref={handleRef}
                        id="handle"
                        tabIndex={0}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}>
                </Handle>

            </JoystickStyled>
        </div>
    );
}

export default Joystick;